<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

// The phone price bar (competitor teardown item 9, site-wide 2026-09-30):
// the price and the way to the request card, fixed at the bottom of a phone
// screen, hidden while the card itself (`target` = its id) is on screen.
// One implementation for boats, experiences, villas, transfers and cars.
const props = defineProps({
  target: { type: String, required: true },   // id of the request card to watch and to jump to
  value:  { type: String, default: '' },      // the price, already formatted (or "Price on request")
  sub:    { type: String, default: '' },      // "per boat", "per person", "per night"…
  cta:    { type: String, required: true }    // button label
})

const hidden = ref(false)
let observer = null
onMounted(() => {
  const el = typeof document !== 'undefined' ? document.getElementById(props.target) : null
  if (typeof IntersectionObserver === 'undefined' || !el) return
  observer = new IntersectionObserver(([entry]) => { hidden.value = entry.isIntersecting }, { threshold: 0.15 })
  observer.observe(el)
})
onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <div class="mbar" :class="{ 'mbar--hidden': hidden }">
    <div v-if="value" class="mbar__price">
      <span class="mbar__v fw-value">{{ value }}</span>
      <span v-if="sub" class="mbar__sub">{{ sub }}</span>
    </div>
    <a :href="`#${target}`" class="mbar__cta">{{ cta }}</a>
  </div>
</template>

<style scoped>
.mbar {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 40;
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
  padding: 0.65rem clamp(1rem, 4vw, 1.5rem) calc(0.65rem + env(safe-area-inset-bottom, 0px));
  background: color-mix(in oklch, var(--surface) 94%, transparent); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid var(--line); box-shadow: 0 -12px 30px -20px color-mix(in oklch, var(--ink) 40%, transparent);
  transition: transform 220ms ease, opacity 220ms ease;
}
.mbar--hidden { transform: translateY(110%); opacity: 0; pointer-events: none; }
.mbar__price { display: grid; line-height: 1.15; min-width: 0; }
.mbar__v { font-family: var(--font-display, var(--display)); font-size: 1.15rem; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mbar__sub { font-size: 0.72rem; color: var(--muted); }
.mbar__cta { flex: none; margin-left: auto; padding: 0.7rem 1.1rem; border-radius: 999px; background: var(--night); color: var(--foam); font-weight: 700; font-size: 0.92rem; text-decoration: none; white-space: nowrap; }
@media (min-width: 880px) { .mbar { display: none; } }
@media (prefers-reduced-motion: reduce) { .mbar { transition: none; } }
</style>
